import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import ImportService from "../../services/import.service";
import { sha256Hex, validateImportFile, XLSX_CONTENT_TYPE } from "./importFile";
import { formatImportDate } from "./importDate";
import useImportPolling from "./useImportPolling";
import ImportStatus from "./components/ImportStatus";
import StagedRun from "./components/StagedRun";
import ExecutionStatus from "./components/ExecutionStatus";
import "./Import.scss";

const initialState = {
  file: null,
  error: null,
  uploading: false,
  verifying: false,
  acting: false,
  restoring: false,
  reservationId: null,
  run: null,
};
const unwrapRun = value => (value && Object.prototype.hasOwnProperty.call(value, "run") ? value.run : value);
const isDryRunComplete = run => Boolean(run && run.state === "COMPLETED_WITH_ERRORS" && run.phase === "DRY_RUN");
const reducer = (state, action) => {
  switch (action.type) {
    case "SELECT":
      return { ...initialState, file: action.file, error: action.error };
    case "UPLOADING":
      return { ...state, uploading: true, error: null };
    case "VERIFYING":
      return { ...state, uploading: false, verifying: true, reservationId: action.reservationId };
    case "RUN":
      return {
        ...state,
        uploading: false,
        verifying: false,
        acting: false,
        restoring: false,
        reservationId: null,
        run: action.run,
      };
    case "RESTORED_NONE":
      return { ...state, restoring: false };
    case "ACTING":
      return { ...state, acting: true, error: null };
    case "RESET":
      return initialState;
    case "ERROR":
      return { ...state, uploading: false, verifying: false, restoring: false, acting: false, error: action.error };
    default:
      return state;
  }
};

const Import = ({ initialRun }) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, { ...initialState, restoring: !initialRun, run: initialRun });
  const [downloading, setDownloading] = useState(false);
  const downloadController = useRef(null);
  useEffect(() => {
    const abort = () => {
      if (downloadController.current) downloadController.current.abort();
      downloadController.current = null;
    };
    const onPageHide = () => {
      abort();
      setDownloading(false);
    };
    window.addEventListener("pagehide", onPageHide);
    return () => {
      abort();
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);
  const load = useCallback(
    () =>
      state.reservationId
        ? ImportService.getReservation(state.reservationId)
        : ImportService.getRun(state.run && state.run.runId),
    [state.reservationId, state.run],
  );
  const onUpdate = useCallback(value => {
    const run = unwrapRun(value);
    if (run) dispatch({ type: "RUN", run });
  }, []);
  const onDenied = useCallback(() => dispatch({ type: "ERROR", error: "This Import run is no longer available." }), []);
  useImportPolling({
    enabled: Boolean(
      state.verifying ||
        (state.run &&
          ![
            "STAGED",
            "REJECTED",
            "CANCELLED",
            "COMPLETED",
            "COMPLETED_WITH_ERRORS",
            "FAILED",
            "RECOVERY_REQUIRED",
          ].includes(state.run.state)),
    ),
    load,
    onUpdate,
    onDenied,
  });

  useEffect(() => {
    if (initialRun) return;
    const continuation = new URLSearchParams(window.location.search).get("continuation");
    const request = continuation ? ImportService.getContinuation(continuation) : ImportService.getActiveRun();
    request
      .then(value => {
        const run = unwrapRun(value);
        dispatch(run ? { type: "RUN", run } : { type: "RESTORED_NONE" });
      })
      .catch(() =>
        dispatch({ type: "ERROR", error: "The current Import status could not be loaded. Please try again." }),
      );
  }, [initialRun]);

  const selectFile = event => {
    const file = event.target.files && event.target.files[0];
    dispatch({ type: "SELECT", file, error: validateImportFile(file) });
  };

  const startNewImport = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("continuation");
    const query = url.searchParams.toString();
    window.history.replaceState(window.history.state, "", `${url.pathname}${query ? `?${query}` : ""}${url.hash}`);
    dispatch({ type: "RESET" });
  };

  const action = async (operation, resetOnSuccess = false) => {
    dispatch({ type: "ACTING" });
    try {
      const run = await operation(state.run.runId, window.crypto.randomUUID());
      dispatch(resetOnSuccess ? { type: "RESET" } : { type: "RUN", run });
    } catch (error) {
      dispatch({ type: "ERROR", error: error.message || String(error) });
    }
  };
  const download = async artifactType => {
    if (downloadController.current) return;
    const controller = new AbortController();
    downloadController.current = controller;
    setDownloading(true);
    try {
      const file = await ImportService.downloadArtifact(state.run.runId, artifactType, controller.signal);
      if (controller.signal.aborted) return;
      const url = URL.createObjectURL(file.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = file.filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error) {
      if (!controller.signal.aborted) {
        dispatch({
          type: "ERROR",
          error:
            error.response && error.response.status === 403
              ? "You no longer have permission to download this report."
              : "The report could not be downloaded. Please try again.",
        });
      }
    } finally {
      if (downloadController.current === controller) {
        downloadController.current = null;
        if (!controller.signal.aborted) setDownloading(false);
      }
    }
  };
  const submit = async event => {
    event.preventDefault();
    const error = validateImportFile(state.file);
    if (error) return dispatch({ type: "ERROR", error });
    dispatch({ type: "UPLOADING" });
    try {
      const sha256 = await sha256Hex(state.file);
      const idempotencyKey = window.crypto.randomUUID();
      const reservation = await ImportService.createReservation(
        { filename: state.file.name, size: state.file.size, contentType: XLSX_CONTENT_TYPE, sha256 },
        idempotencyKey,
      );
      await ImportService.uploadToS3(reservation.upload, state.file);
      dispatch({ type: "VERIFYING", reservationId: reservation.reservationId });
    } catch (uploadError) {
      dispatch({ type: "ERROR", error: uploadError.message || String(uploadError) });
    }
    return undefined;
  };

  const processing = state.run && ["DRY_RUN_QUEUED", "PARSING", "VALIDATING"].includes(state.run.state);
  const dryRunComplete = isDryRunComplete(state.run);
  let submitLabel = "Dry run";
  if (state.uploading) submitLabel = "Uploading…";
  else if (state.verifying) submitLabel = "Verifying…";
  const selectedFileSize = state.file ? `${Math.ceil(state.file.size / 1024)} KB` : null;
  return (
    <section className="import-access-shell" data-testid="import-access-shell">
      <h2>{t("DashboardContainerTabs.Import")}</h2>
      {state.restoring && <p role="status">Loading current Import status…</p>}
      {!state.run && !state.restoring && (
        <div className="import-workspace import-upload-workspace" data-testid="import-upload">
          <div className="import-upload-content">
            <h3>Import Build to Suit properties</h3>
            <p className="import-upload-description">
              Upload the approved Excel workbook to validate property rows before importing.
              <br />
A dry run validates your workbook without importing properties. You can review the results in DPM
              before you proceed.
            </p>
            <form className="import-upload-form" onSubmit={submit}>
              <label className="import-workbook-label" htmlFor="import-workbook">
                Workbook
              </label>
              <div className="import-file-picker">
                <span className="import-workbook-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" focusable="false">
                    <path d="M6 2.75h8l4.25 4.5v13H6z" />
                    <path d="M14 2.75v4.5h4.25M9 11h6M9 14h6M9 17h4" />
                  </svg>
                </span>
                <span className={state.file ? "import-selected-filename" : "import-file-placeholder"}>
                  {state.file ? state.file.name : "Choose a workbook"}
                </span>
                {selectedFileSize && <span className="import-selected-filesize">{selectedFileSize}</span>}
                <input
                  id="import-workbook"
                  className="import-file-input"
                  type="file"
                  onChange={selectFile}
                  aria-describedby="import-workbook-hint"
                />
              </div>
              <p id="import-workbook-hint" className="import-upload-hint">
                Accepted format: .xlsx&nbsp; · &nbsp;Build to Suit worksheet
              </p>
              {state.error && (
                <div className="import-upload-error" role="alert">
                  {state.error}
                </div>
              )}
              <div className="import-upload-actions">
                <button
                  type="submit"
                  className="import-button-primary"
                  disabled={!state.file || Boolean(state.error) || state.uploading || state.verifying}
                >
                  {submitLabel}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {state.verifying && !state.run && <p data-testid="import-verifying">Verifying your uploaded workbook…</p>}
      {state.run &&
        !["REJECTED", "CANCELLED", "COMPLETED", "COMPLETED_WITH_ERRORS", "FAILED"].includes(state.run.state) &&
        !["DRY_RUN_QUEUED", "PARSING", "VALIDATING", "STAGED", "EXECUTION_QUEUED", "EXECUTING"].includes(
          state.run.state,
        ) && <p data-testid="import-accepted">Your upload was accepted.</p>}
      {processing && (
        <div className="import-workspace" data-testid="import-processing">
          <section className="import-processing-card" aria-live="polite">
            <h3>Your dry run is being executed</h3>
            <p>We will email you when your results are ready to review in DPM.</p>
            {state.run && (
              <p className="import-file-meta">
                {state.run.filename}
                {state.run.submittedAt && ` · Submitted ${formatImportDate(state.run.submittedAt)}`}
              </p>
            )}
            <p>
              You can leave this page or close your browser. Validation will continue.
              <br />
              Return to Admin &gt; Import at any time to see your current status.
            </p>
          </section>
        </div>
      )}
      {/* {state.run && <ContinuationLink url={state.run.continuationUrl} />} */}
      <ImportStatus run={state.run} onNewUpload={startNewImport} />
      {downloading && <p role="status">Downloading report...</p>}
      {state.run && state.error && <div role="alert">{state.error}</div>}
      {state.run && state.run.state === "STAGED" && (
        <StagedRun
          run={state.run}
          busy={state.acting || downloading}
          onProceed={() => action(ImportService.proceed)}
          onCancel={() => action(ImportService.cancel, true)}
          onDownload={download}
        />
      )}
      {dryRunComplete && (
        <StagedRun
          run={state.run}
          dryRunComplete
          busy={state.acting || downloading}
          onProceed={() => action(ImportService.proceed)}
          onCancel={() => action(ImportService.cancel, true)}
          onDownload={download}
        />
      )}
      <ExecutionStatus
        run={dryRunComplete ? null : state.run}
        onNewImport={startNewImport}
        onDownload={download}
        onComplete={() => action(ImportService.complete, true)}
        busy={state.acting || downloading}
      />
    </section>
  );
};

Import.propTypes = { initialRun: PropTypes.shape({ runId: PropTypes.string, state: PropTypes.string }) };
Import.defaultProps = { initialRun: null };

export default Import;
